/**
 * Operation Dashboard - 2x2 anomaly snapshot.
 * Polls every 30s via React Query; pauses when tab is backgrounded.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Typography, IconButton, Tooltip, Chip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { startOfYear } from 'date-fns';

import DashboardFilterBar, { DashboardFilters } from '../Dashboard/DashboardFilterBar';
import QuadrantCard from './QuadrantCard';
import { fetchOperationSummary } from './operationDashboardApi';
import type { Quadrant, AnomalyResult, OperationSummaryResponse } from './types';


const DEFAULT_FILTERS: DashboardFilters = {
  brands: [], categories: [], stores: [], cities: [],
  fromDate: startOfYear(new Date()),
  toDate: new Date(),
};


function groupByQuadrant(anomalies: AnomalyResult[]): Record<Quadrant, AnomalyResult[]> {
  const out: Record<Quadrant, AnomalyResult[]> = {
    forecasts: [], orders: [], inventory: [], sales: [],
  };
  for (const a of anomalies) out[a.quadrant].push(a);
  return out;
}


const OperationDashboard: React.FC = () => {
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);

  const {
    data, isLoading, isFetching, error, dataUpdatedAt, refetch,
  } = useQuery<OperationSummaryResponse>({
    queryKey: ['operation-dashboard', filters],
    queryFn: () => fetchOperationSummary(filters),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 25_000,
  });

  const byQuadrant = useMemo(
    () => groupByQuadrant(data?.anomalies ?? []),
    [data]
  );

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : '—';

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', gap: 2 }}>
        <Typography sx={{
          fontSize: 'var(--text-xl)', fontWeight: 600,
          color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)',
        }}>
          Operation Dashboard
        </Typography>
        <Typography sx={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
          Anomaly snapshot — last updated {lastUpdated}
        </Typography>
        <Tooltip title="Refresh now">
          <span>
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        {error ? (
          <Chip
            size="small"
            label="Last refresh failed — retrying"
            sx={{ bgcolor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
          />
        ) : null}
      </Box>

      <DashboardFilterBar filters={filters} onChange={setFilters} />

      {isLoading ? (
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Box sx={{
                height: 220, borderRadius: 'var(--radius-md, 6px)',
                border: '1px solid var(--color-border, #ddd)',
                bgcolor: 'var(--color-bg-tertiary, #EDEDE8)',
              }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <QuadrantCard title="Forecasts" quadrant="forecasts" filters={filters} anomalies={byQuadrant.forecasts} />
          </Grid>
          <Grid item xs={12} md={6}>
            <QuadrantCard title="Order Management" quadrant="orders" filters={filters} anomalies={byQuadrant.orders} />
          </Grid>
          <Grid item xs={12} md={6}>
            <QuadrantCard title="Inventory" quadrant="inventory" filters={filters} anomalies={byQuadrant.inventory} />
          </Grid>
          <Grid item xs={12} md={6}>
            <QuadrantCard title="Sales" quadrant="sales" filters={filters} anomalies={byQuadrant.sales} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default OperationDashboard;
